import { Auth } from 'aws-amplify';
import { useEffect } from 'react';

import { getAuthIdToken } from '../utils/auth';
import { getDomainPrefix } from '../utils/branding';
import { buildCrossDomainUrl, extractDomainPrefixFromToken } from '../utils/domainRedirect';

const FALLBACK_BRAND_RECOVERY_URL = 'https://loop.homes/loop-optimise/';

export interface AuthDomainPrefixGuardOptions {
  claimKey?: string;
  fallbackClaimKey?: string;
  missingRedirectPath?: string;
  signOutOnMissing?: boolean;
  redirectOnMismatch?: boolean;
  requireClaim?: boolean;
  enabled?: boolean;
  logPayloadOnce?: boolean;
}

export function useAuthDomainPrefixGuard({
  claimKey = 'custom:domain_prefix',
  fallbackClaimKey = 'domain_prefix',
  missingRedirectPath = FALLBACK_BRAND_RECOVERY_URL,
  signOutOnMissing = true,
  redirectOnMismatch = true,
  requireClaim = false,
  enabled = true,
  logPayloadOnce = true,
}: AuthDomainPrefixGuardOptions = {}) {
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const run = async () => {
      // Get and decode ID token
      let idToken;
      try {
        idToken = await getAuthIdToken();
      } catch (authError) {
        console.error('[AuthDomainPrefixGuard] Failed to get ID token:', authError);
        return; // Can't proceed without token
      }

      if (!idToken) {
        return;
      }

      // Decode token payload
      let payload: Record<string, unknown>;
      try {
        payload = typeof idToken.decodePayload === 'function' ? idToken.decodePayload() : {};
      } catch (decodeError) {
        console.error('[AuthDomainPrefixGuard] Failed to decode token payload:', decodeError);
        return;
      }

      const tokenPrefix = extractDomainPrefixFromToken(payload, claimKey, fallbackClaimKey);
      const domainPrefix = getDomainPrefix();

      // Handle missing domain prefix claim
      if (!tokenPrefix) {
        if (!requireClaim) {
          return;
        }

        if (signOutOnMissing) {
          try {
            await Auth.signOut();
          } catch (signOutError) {
            console.error('[AuthDomainPrefixGuard] Failed to sign out user:', signOutError);
            // Continue with redirect anyway
          }
        }

        if (!cancelled && typeof window !== 'undefined') {
          window.location.replace(missingRedirectPath);
        }
        return;
      }

      // Handle domain prefix mismatch
      if (tokenPrefix !== domainPrefix && redirectOnMismatch && typeof window !== 'undefined') {
        try {
          const url = buildCrossDomainUrl(tokenPrefix, window.location);
          window.location.replace(url);
        } catch (urlError) {
          console.error('[AuthDomainPrefixGuard] Failed to build or redirect to cross-domain URL:', {
            error: urlError,
            tokenPrefix,
            domainPrefix,
            currentUrl: window.location.href,
          });
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    claimKey,
    fallbackClaimKey,
    missingRedirectPath,
    signOutOnMissing,
    redirectOnMismatch,
    requireClaim,
    enabled,
    logPayloadOnce,
  ]);
}

export default useAuthDomainPrefixGuard;
